import { useEffect, useState } from 'react';
import { FileSpreadsheet, Plus, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { AdminGuard } from '../components/guards';
import { FileUpload, ImportBatchCard } from '../components/imports';
import { useImportStore } from '../store/importStore';

/**
 * Import management page — upload CSVs, view batch history
 */
export const ImportManagement = () => {
  const { batches, isLoading, error, clearError, fetchBatches, uploadCsvs, pollBatchStatus, deleteBatch } =
    useImportStore();

  const [showUpload, setShowUpload] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const handleStartProcessing = async () => {
    if (selectedFiles.length === 0) return;

    try {
      setIsProcessing(true);
      const batch = await uploadCsvs('CLELL_WADE', selectedFiles);
      toast.success('Files uploaded — matching in progress...');
      setSelectedFiles([]);
      setShowUpload(false);

      // Poll until matching completes
      const result = await pollBatchStatus(batch.id);
      if (result.status === 'REVIEW') {
        toast.success('Matching complete! Ready for review.');
      } else if (result.status === 'FAILED') {
        toast.error('Processing failed. Check batch details.');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const sortedBatches = [...batches].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <AdminGuard>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Page header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <FileSpreadsheet className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Data Imports
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Import coach directories and manage school matching
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowUpload(!showUpload)}
              className="btn-primary"
              disabled={isProcessing}
            >
              {showUpload ? (
                <ChevronUp className="h-4 w-4 mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              New Import
            </button>
          </div>

          {/* Error banner */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between">
              <span className="text-red-800 dark:text-red-300">{error}</span>
              <button
                onClick={clearError}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Upload panel */}
          {showUpload && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Upload CSV Files
              </h2>

              {/* Source selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Source
                </label>
                <select
                  disabled
                  className="block w-full sm:w-64 rounded-lg border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                >
                  <option value="CLELL_WADE">Clell Wade</option>
                </select>
              </div>

              {/* File upload zone */}
              <FileUpload
                onFilesSelected={setSelectedFiles}
                disabled={isProcessing}
              />

              {/* Start button */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleStartProcessing}
                  disabled={selectedFiles.length === 0 || isProcessing}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isProcessing ? (
                    <span className="flex items-center">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    'Start Processing'
                  )}
                </button>
                {isProcessing && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Uploading and matching schools...
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Batches list */}
          {isLoading && batches.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500 dark:text-gray-400">Loading batches...</span>
            </div>
          ) : sortedBatches.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p>No imports yet. Click "New Import" to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedBatches.map((batch) => (
                <ImportBatchCard key={batch.id} batch={batch} onDelete={async (id) => {
                  try {
                    await deleteBatch(id);
                    toast.success('Import deleted');
                  } catch {
                    toast.error('Failed to delete import');
                  }
                }} />
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminGuard>
  );
};
